/*
 * ChatbotPreparing — full-screen progress card for the chatbot
 * first-touch crawl (Phase 5).
 *
 * Mounted at /marketplace/chatbot/preparing. The user lands here
 * the first time they click the AI Chatbot card in /marketplace
 * (when the KB hasn't been built yet). We poll the prepare/status
 * endpoint every 2.5 s and:
 *
 *   - render a progress bar driven by `progress.percentage`
 *   - show the current crawl message ("Reading 12 of ~80 pages")
 *   - on completion, redirect to /marketplace/chatbot
 *   - on failure, surface a retry option that re-fires /prepare
 *
 * Single component, no external state lib, no SSE. Polling is fine
 * here — typical crawl is 30–90 s, we don't need sub-second updates.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Sparkles, Loader2, ChevronLeft, RotateCw, Globe } from 'lucide-react';


const API_BASE = process.env.REACT_APP_API_URL ?? '';
const POLL_INTERVAL_MS = 2500;


interface PrepareStatus {
  status: 'pending' | 'crawling' | 'embedding' | 'in_progress' | 'completed' | 'failed';
  message: string;
  progress: {
    pages_scraped: number;
    pages_total: number;
    percentage: number;
  } | null;
  already_prepared?: boolean;
  website?: string | null;
}


export default function ChatbotPreparing() {
  const navigate = useNavigate();
  const [state, setState] = useState<PrepareStatus | null>(null);
  const [retrying, setRetrying] = useState(false);
  const stopPollingRef = useRef(false);

  const poll = useCallback(async () => {
    try {
      const { data } = await axios.get<PrepareStatus>(
        `${API_BASE}/api/marketplace/chatbot/prepare/status`,
      );
      setState(data);
      if (data.status === 'completed' || data.already_prepared) {
        stopPollingRef.current = true;
        // Quick beat to let users see "Done." land, then redirect.
        setTimeout(() => navigate('/marketplace/chatbot'), 600);
      }
    } catch (err) {
      console.error('[chatbot/preparing] status poll failed', err);
    }
  }, [navigate]);

  // Kick off first poll immediately, then on an interval.
  useEffect(() => {
    poll();
    const id = window.setInterval(() => {
      if (!stopPollingRef.current) poll();
    }, POLL_INTERVAL_MS);
    return () => {
      window.clearInterval(id);
      stopPollingRef.current = true;
    };
  }, [poll]);

  const handleRetry = async () => {
    if (retrying) return;
    setRetrying(true);
    try {
      await axios.post(`${API_BASE}/api/marketplace/chatbot/prepare`);
      stopPollingRef.current = false;
      poll();
    } catch (err) {
      console.error('[chatbot/preparing] retry failed', err);
    } finally {
      setRetrying(false);
    }
  };

  const percentage = state?.progress?.percentage ?? 0;
  const isFailed = state?.status === 'failed';
  const isDone = state?.status === 'completed' || state?.already_prepared;

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f8f9fb',
        padding: 24,
      }}
    >
      <div
        style={{
          maxWidth: 520,
          width: '100%',
          background: '#fff',
          border: '1px solid #e2e5eb',
          borderRadius: 16,
          padding: '40px 40px 32px',
          boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
          fontFamily: "var(--font-sans, 'IBM Plex Sans', system-ui, sans-serif)",
        }}
      >
        <button
          type="button"
          onClick={() => navigate('/marketplace')}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            background: 'transparent',
            border: 0,
            color: '#545454',
            fontSize: 12,
            cursor: 'pointer',
            padding: 0,
            marginBottom: 20,
          }}
        >
          <ChevronLeft size={14} strokeWidth={2} />
          Back to marketplace
        </button>

        <div
          aria-hidden="true"
          style={{
            width: 56,
            height: 56,
            borderRadius: 14,
            background: 'rgba(64,128,242,0.10)',
            color: '#4080f2',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 18,
          }}
        >
          {isDone ? <Sparkles size={26} /> : <Loader2 size={26} className="animate-spin" />}
        </div>

        <h1
          style={{
            fontSize: 22,
            fontWeight: 700,
            letterSpacing: '-0.01em',
            color: '#000',
            margin: '0 0 8px',
            lineHeight: 1.3,
          }}
        >
          {isDone
            ? 'Your assistant is ready.'
            : isFailed
            ? "Couldn't finish reading the site."
            : 'Setting up your assistant.'}
        </h1>

        <p
          style={{
            fontSize: 14,
            color: '#545454',
            lineHeight: 1.55,
            margin: '0 0 24px',
          }}
        >
          {isDone
            ? 'Routing you to deploy.'
            : isFailed
            ? "We hit a snag on the crawl. You can retry or open the assistant to add knowledge manually."
            : (state?.message
                || "Reading your website to train the bot. Usually 30 to 90 seconds.")}
        </p>

        {!isFailed && (
          <>
            <div
              style={{
                position: 'relative',
                width: '100%',
                height: 6,
                background: '#eef3ff',
                borderRadius: 9999,
                overflow: 'hidden',
                marginBottom: 12,
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  bottom: 0,
                  width: `${Math.max(percentage, isDone ? 100 : 4)}%`,
                  background: '#4080f2',
                  transition: 'width 0.4s ease',
                }}
              />
            </div>

            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: 11,
                fontFamily: "var(--font-sans, 'IBM Plex Sans', system-ui, sans-serif)",
                color: '#545454',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
              }}
            >
              <span>
                {state?.progress?.pages_scraped ?? 0}
                {state?.progress?.pages_total
                  ? ` / ${state.progress.pages_total}`
                  : ''}{' '}
                pages
              </span>
              <span>{isDone ? '100%' : `${percentage}%`}</span>
            </div>

            {state?.website && (
              <p
                style={{
                  marginTop: 18,
                  fontSize: 12,
                  color: '#545454',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <Globe size={12} strokeWidth={2} />
                {state.website}
              </p>
            )}
          </>
        )}

        {isFailed && (
          <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
            <button
              type="button"
              onClick={handleRetry}
              disabled={retrying}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '9px 14px',
                background: '#4080f2',
                color: '#fff',
                border: 0,
                borderRadius: 10,
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {retrying ? <Loader2 size={14} className="animate-spin" /> : <RotateCw size={14} />}
              Try again
            </button>
            <button
              type="button"
              onClick={() => navigate('/marketplace/chatbot')}
              style={{
                padding: '9px 14px',
                background: '#fff',
                color: '#000',
                border: '1px solid #dedede',
                borderRadius: 10,
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Skip to assistant
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
